import {
    Alert,
    FormHelperText,
    InputLabel,
    TextField,
    Typography
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {Box, Button, FormControl, MenuItem, Select} from '@mui/material';
import React, {useCallback, useState} from 'react';
import CustomModal from '../CustomModal';
import {useFormHelper} from '../Form/useFormHelper';
import {blockedEmailDomains, generatePassword, importUsers} from './UserAccountImport';

const emailRegEx = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

const validators = {
    firstName: (value) => {
        const errorMessages = [];

        if (typeof value !== 'string' || value.trim() === '') {
            errorMessages.push('Bitte geben Sie den Vorname an');
        }

        return errorMessages;
    },
    lastName: (value) => {
        const errorMessages = [];

        if (typeof value !== 'string' || value.trim() === '') {
            errorMessages.push('Bitte geben Sie den Nachname an');
        }

        return errorMessages;
    },
    email: (value) => {
        const errorMessages = [];

        if (typeof value !== 'string' || value.trim() === '') {
            errorMessages.push('Bitte geben Sie die E-Mail-Adresse an');
        }

        if (typeof value !== 'string') {
            return;
        }

        if (!emailRegEx.test(value)) {
            errorMessages.push('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        }

        const valueTrimmed = value.trim()
        const domain = valueTrimmed.substring(valueTrimmed.indexOf('@'));
        if (domain === '@drv-bund.de') {
            errorMessages.push('Personen mit einer "@drv-bund.de" E-Mail-Adresse nutzen die Plattform über den Dienstausweis (SSO)');
        }

        const blockedDomains = blockedEmailDomains.filter(domain => domain !== 'drv-bund.de');
        if (blockedDomains.includes(domain.substring(1))) {
            errorMessages.push(`Personen mit einer "${domain}" E-Mail-Adresse müssen sich selbst registrieren.`);
        }

        return errorMessages;
    },
};

const FormErrors = ({name, formErrors}) => {
    const errors = formErrors[name] ?? undefined;

    if (errors === undefined || errors.length === 0) {
        return null;
    }

    return errors.map((error, index) => <FormHelperText key={index}>{error}</FormHelperText>);
};

const useCreateExternalReferentAccount = ()  => {
    const {enqueueSnackbar} = useSnackbar();
    const [isPending, setIsPending] = useState(false);
    const {data, reset, validate, ...useFormHelperReturn} = useFormHelper({
        defaultValues: {
            salutation: 'n',
            title: '',
            firstName: '',
            lastName: '',
            email: '',
        },
        validators,
        validateOn: 'blur',
    });

    const submit = useCallback(() => {
        if (!validate()) {
            return Promise.reject('Validation failed');
        }

        return new Promise((resolve, reject) => {
            const user = {...data, password: generatePassword()};

            setIsPending(true);
            importUsers([user]).then((result) => {
                if (!result || result.length === 0) {
                    enqueueSnackbar('Beim Anlegen ist leider etwas schiefgelaufen :(', {variant: 'error'});
                    setIsPending(false);
                    reject('failed_to_create_user');
                    return;
                }

                if (result[0].result !== 'created') {
                    enqueueSnackbar('Für diese Person existiert bereits ein Benutzerkonto', {variant: 'warning'});
                    reset();
                    setIsPending(false);
                    reject('user_already_exists');
                    return;
                }

                enqueueSnackbar('Account erfolgreich angelegt', {variant: 'success'});
                reset();
                setIsPending(false);
                resolve(user);
            }).catch(() => {
                setIsPending(false);
                enqueueSnackbar('Beim Anlegen ist leider etwas schiefgelaufen :(', {variant: 'error'});
                reject('failed_to_create_user');
            });
        });
    }, [validate, data, enqueueSnackbar, reset]);

    return {
        data,
        reset,
        validate,
        ...useFormHelperReturn,
        isPending,
        submit,
    };
};

const generateMailTo = (user) => {
    const nameParts = [];
    if (user.salutation === 'f') {
        nameParts.push('Frau');
    }
    if (user.salutation === 'm') {
        nameParts.push('Herr');
    }
    if (user.title) {
        nameParts.push(user.title);
    }
    nameParts.push(user.firstName, user.lastName);

    const mailBody = [
        'Guten Tag ' + nameParts.filter(v => v.trim() !== '').join(' ') + ',\n',
        'wir haben für Sie ein Konto auf unserer DRV Bund Lernwelt (https://lernwelt.drv-bund.de) eingerichtet.',
        'Bitte melden Sie sich dort mit dem folgenden Initialpasswort an:\n',
        user.password + '\n\n',
        'Nach der erstmaligen Anmeldung werden Sie aufgefordert die Nutzungsbedingungen zu akzeptieren und anschließend ein neues Passwort zu vergeben.',
    ].join('\n');
    return 'mailto:' + user.email + '?subject=' + encodeURIComponent('Ihr Konto für die DRV Bund Lernwelt') + '&body=' + encodeURIComponent(mailBody);
};

export const CreateExternalReferentAccountModal = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const {data, errors, reset, isPending, hasErrors, hasFieldErrors, onChange, onBlur, submit} = useCreateExternalReferentAccount();
    const [user, setUser] = useState(null);
    const [mailtoButtonClicked, setMailtoButtonClicked] = useState(false);

    const handleClose = () => {
        if (user) {
            return;
        }

        reset();
        setModalOpen(false);
    };

    const handleSubmit = () => {
        submit().then((user) => setUser(user)).catch(() => {});
    };

    window.openCreateExternalReferentAccount = () => setModalOpen(true);

    return <CustomModal
        maxWidth={'sm'}
        open={modalOpen}
        onClose={handleClose}
        title={'Konto für externe Dozierende anlegen'}
        actions={user ? undefined : <>
            <Button variant={'text'} onClick={handleClose} disabled={isPending}>Abbrechen</Button>
            <Button variant={'contained'} onClick={handleSubmit} disabled={isPending || hasErrors}>
                {isPending ? 'Konto wird angelegt ...' : 'Anlegen'}
            </Button>
        </>}
    >
        {user && <>
            <Alert severity={'error'}>
                <Typography variant={'body2'}>Es wurde ein automatisch generiertes Passwort für dieses Konto vergeben. Dieses kann ausschließlich über nachfolgende Schaltfläche versendet werden. Diese öffnet in HCL Notes eine neue E-Mail.</Typography>
            </Alert>
            <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                <Button href={generateMailTo(user)} variant={mailtoButtonClicked ? 'outlined' : 'contained'} onClick={() => setMailtoButtonClicked(true)}>Initalpasswort senden</Button>
            </Box>
            {mailtoButtonClicked && <>
                <Alert severity={'warning'}>
                    <Typography variant={'body2'}>Da vom System nicht erkannt werden kann, ob die E-Mail erfolgreich versandt wurde, ist eine Bestätigung über nachfolgende Schaltfläche notwendig.</Typography>
                </Alert>
                <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                    <Button variant={'contained'} color={'success'} onClick={() => {setMailtoButtonClicked(false); setUser(null)}}>Bestätigen</Button>
                </Box>
            </>}
        </>}
        {!user && <form>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 1}}>
                <FormControl fullWidth>
                    <InputLabel>Anrede</InputLabel>
                    <Select
                        name={'salutation'}
                        label={'Anrede'}
                        value={data.salutation}
                        onChange={onChange}>
                        <MenuItem value={'n'}>Keine Angabe</MenuItem>
                        <MenuItem value={'f'}>Frau</MenuItem>
                        <MenuItem value={'m'}>Herr</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <TextField name={'title'} label={'Titel'} value={data.title} onChange={onChange} />
                </FormControl>
                <FormControl fullWidth error={hasFieldErrors('firstName')}>
                    <TextField
                        name={'firstName'}
                        label={'Vorname*'}
                        value={data.firstName}
                        error={hasFieldErrors('firstName')}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                    <FormErrors name={'firstName'} formErrors={errors}/>
                </FormControl>
                <FormControl fullWidth error={hasFieldErrors('lastName')}>
                    <TextField
                        name={'lastName'}
                        label={'Nachname*'}
                        error={hasFieldErrors('lastName')}
                        value={data.lastName}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                    <FormErrors name={'lastName'} formErrors={errors}/>
                </FormControl>
                <FormControl fullWidth error={hasFieldErrors('email')}>
                    <TextField
                        name={'email'}
                        label={'E-Mail*'}
                        value={data.email}
                        error={hasFieldErrors('email')}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                    <FormErrors name={'email'} formErrors={errors}/>
                </FormControl>
                <Typography variant={'body2'} color={'textSecondary'}>* Pflichtfeld</Typography>
            </Box>
        </form>}
    </CustomModal>;
};
